import React, { useEffect, useState, useRef } from 'react';
import { useTenancy } from '@/contexts/TenancyContext';
import { useMSGReadOnly } from '../../contexts/ReadOnlyContext';
import { db } from '@/js/external-js/config/main_config';
import { format } from 'date-fns';
import { Plus, Pencil, Trash2, Save, X, Eye } from 'lucide-react';

import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

import EditableField from '@/components/ui/EditableField';
import { FrameworkBadge } from '@/app/pages/msg/components/FrameworkBadge';
import { fetchPageContentMDX, populateCustomContent, fetchRelatedDocuments } from '@/app/pages/msg/utils/common';
import { SingleUserCombobox } from '@/components/ui/SingleUserCombobox';
import specialInterestGroupsService, { SpecialInterestGroup } from '@/services/specialInterestGroupsService';
import { getAuthUID } from '@/lib/authState';
import RelatedClausesAccordianItem from '@/app/pages/msg/components/RelatedClauses';
import { getAllModules } from '@/app/pages/msg/utils/common';
import { MarkdownRenderer } from '@/app/pages/msg/components/MarkdownRenderer';
import { useMarkdownComponents } from '@/app/pages/msg/components/MDXFields';
import SupportingDocs from '../../components/SupportingDocs';
import AdditionalContent from '../../components/AdditionalContent';
import FootNote from '../../components/FootNote';
import { useMSGVersion } from '../../hooks/useMSGVersion';
import SupportingDocsDropdown from '../../components/SupportingDocsDropdown';

export default function SpecialInterestGroupsSection() {
  const { activeTenancy, userIdToName } = useTenancy();
  const { isReadOnly } = useMSGReadOnly();
  const { incrementVersion } = useMSGVersion();


  const clauseStandards = useRef<Set<string>>(new Set());
  const [pageClauses, setPageClauses] = useState<Set<string>>(new Set());
  const [modulesList, setModulesList] = useState([]);

  // Function to update pageClauses
  const updatePageClauses = (newClauses: Set<string>) => {
    setPageClauses(new Set(newClauses));
  };

  // Special Interest Groups State
  const [groups, setGroups] = useState<SpecialInterestGroup[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<SpecialInterestGroup | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form State
  const [formData, setFormData] = useState({
    groupName: '',
    primaryContact: '',
    contactEmail: '',
    contactPhone: '',
    internalOwner: '',
    comments: '',
  });

  // MDX Things
  const [pageTemplateMDX, setPageTemplateMDX] = React.useState<string>('');
  const [supportingDocs, setSupportingDocs] = React.useState([]);
  const [sortedFrameworks, setSortedFrameworks] = React.useState([]);
  const [dynamicStandardNames, setDynamicStandardNames] = useState<Map<string, string>>(new Map());
  const [dynamicContent, setDynamicContent] = React.useState<string>("");

  const pageName = 'context';
  const contentPath = ['specialInterestGroups_MDX'];
  const pageRoute = '/MSG/context/specialInterestGroups';

  // Load special interest groups
  const loadGroups = async () => {
    if (!activeTenancy.id) return;

    try {
      setIsLoading(true);
      const data = await specialInterestGroupsService.getSpecialInterestGroups(activeTenancy.id);
      setGroups(data);
    } catch (error) {
      console.error('Error loading special interest groups:', error);
      toast.error('Failed to load special interest groups');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle form input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle owner selection from SingleUserCombobox
  const handleOwnerSelect = (userId: string) => {
    setFormData(prev => ({
      ...prev,
      internalOwner: userId
    }));
  };

  // Reset form and editing state
  const resetForm = () => {
    setFormData({
      groupName: '',
      primaryContact: '',
      contactEmail: '',
      contactPhone: '',
      internalOwner: '',
      comments: '',
    });
    setEditingGroup(null);
  };

  // Open dialog for adding a new group
  const handleAddClick = () => {
    resetForm();
    setIsDialogOpen(true);
  };

  // Open dialog for editing an existing group
  const handleEditClick = (group: SpecialInterestGroup) => {
    setEditingGroup(group);
    setFormData({
      groupName: group.groupName || '',
      primaryContact: group.primaryContact || '',
      contactEmail: group.contactEmail || '',
      contactPhone: group.contactPhone || '',
      internalOwner: group.internalOwner || '',
      comments: group.comments || '',
    });
    setIsDialogOpen(true);
  };

  // Handle form submission (add or update)
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeTenancy.id) return;

    try {
      setIsSubmitting(true);
      const userId = await getAuthUID();

      if (editingGroup && editingGroup.id) {
        // Update existing group
        await specialInterestGroupsService.updateSpecialInterestGroup(
          activeTenancy.id,
          editingGroup.id,
          {
            ...formData,
            updatedBy: userId,
          }
        );
        await incrementVersion(
          'special_interest_group_update',
          `Updated special interest group for ${editingGroup.id}`,
          {
            groupId: editingGroup.id,
            oldData: editingGroup,
            newData: formData
          }
        );
        toast.success('Special interest group updated successfully');
      } else {
        // Add new group
        await specialInterestGroupsService.createSpecialInterestGroup(
          activeTenancy.id,
          {
            ...formData,
            addedBy: userId,
            updatedBy: userId,
          }
        );
        await incrementVersion(
          'special_interest_group_create',
          `Created special interest group for ${formData.groupName}`,
          {
            groupId: formData.groupName,
            newData: formData
          }
        );
        toast.success('Special interest group added successfully');
      }

      await loadGroups();
      setIsDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error('Error saving special interest group:', error);
      toast.error('Failed to save special interest group');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle group deletion
  const handleDeleteClick = async (id: string) => {
    if (!activeTenancy.id || !window.confirm('Are you sure you want to delete this group?')) return;

    try {
      await specialInterestGroupsService.deleteSpecialInterestGroup(activeTenancy.id, id);
      await loadGroups();
      await incrementVersion(
        'special_interest_group_delete',
        `Deleted special interest group for ${id}`,
        {
          groupId: id
        }
      );
      toast.success('Special interest group deleted successfully');
    } catch (error) {
      console.error('Error deleting special interest group:', error);
      toast.error('Failed to delete special interest group');
    }
  };

  // Load initial data
  useEffect(() => {
    if (activeTenancy.id === "") return;

    const loadModules = async () => {
      const allModules = await getAllModules();
      const storedVisibilities = JSON.parse(localStorage.getItem('modulesVisibility') || '{}');

      allModules.forEach(module => {
        module.visible = storedVisibilities[module.id] ?? false;
      });

      setModulesList(allModules);
    }

    const loadInitialData = async () => {
      await loadGroups();

      // Load page content and other data
      const getPageContentMDX = fetchPageContentMDX(db, pageName, contentPath, activeTenancy, setPageTemplateMDX);
      const getRelatedDocuments = fetchRelatedDocuments(db, activeTenancy.id, pageRoute, setSupportingDocs);
      // const popStandardsList = populateStandardsList(db, pageName, contentPath, setDynamicStandardNames, setSortedFrameworks);
      const getCustomContent = populateCustomContent(db, activeTenancy.id, pageRoute, setDynamicContent);

      loadModules();

      return () => {
        getPageContentMDX();
        getCustomContent();
        getRelatedDocuments();
      };
    };

    loadInitialData();
  }, [activeTenancy.id]);

  return (
    <div className="space-y-6">
      <div className="bg-card rounded-xl border border-border shadow-sm px-6 py-6 relative">
        <div className="absolute right-4 top-4 z-10">
          <div className="flex items-center space-x-2">
            <SupportingDocsDropdown pageRoute={pageRoute} clauseStandards={clauseStandards} pageClauses={pageClauses} />
            <RelatedClausesAccordianItem modulesList={modulesList} clauseStandards={clauseStandards} setModulesList={setModulesList} />
          </div>
        </div>
        <div className="px-6 py-6">
          <MarkdownRenderer source={pageTemplateMDX} components={useMarkdownComponents(modulesList, clauseStandards, { current: pageClauses }, updatePageClauses)} />
        </div>
        <div style={{ maxWidth: '56rem', marginLeft: 'auto', marginRight: 'auto', padding: '0px 1rem 1.5rem', transition: 'color 0.2s, background-color 0.2s', backgroundColor: 'transparent' }}>
          <AdditionalContent pageRoute={pageRoute} />
          <FootNote />
        </div>
      </div>

      <div className="bg-card rounded-xl border border-border shadow-sm px-6 py-6 mt-4">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-semibold text-foreground">Special Interest Groups</h2>
          {!isReadOnly && (
            <Button onClick={handleAddClick}>
              <Plus className="mr-2 h-4 w-4" /> Add Group
            </Button>
          )}
        </div>

        <div className="space-y-4">
          {isLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : groups.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No special interest groups found. Click "Add Group" to create one.
            </div>
          ) : (
            <Table className="table">
              <TableHeader>
                <TableRow>
                  <TableHead>Group Name</TableHead>
                  <TableHead>Primary Contact</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Internal Owner</TableHead>
                  <TableHead>Last Updated</TableHead>
                  <TableHead className="w-[100px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {groups.map((group) => (
                  <TableRow key={group.id}>
                    <TableCell className="font-medium">{group.groupName}</TableCell>
                    <TableCell>{group.primaryContact}</TableCell>
                    <TableCell>{group.contactEmail}</TableCell>
                    <TableCell>{group.contactPhone}</TableCell>
                    <TableCell>
                      {group.internalOwner ? userIdToName[group.internalOwner] || group.internalOwner : '-'}
                    </TableCell>
                    <TableCell>
                      {group.lastUpdated
                        ? format(new Date(group.lastUpdated), 'MMM d, yyyy')
                        : '-'}
                    </TableCell>
                    <TableCell>
                      <div className="flex space-x-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEditClick(group)}
                        >
                          {isReadOnly ? (
                            <Eye className="h-4 w-4" />
                          ) : (
                            <Pencil className="h-4 w-4" />
                          )}
                        </Button>
                        {!isReadOnly && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => group.id && handleDeleteClick(group.id)}
                            className="text-destructive hover:text-destructive/80"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
      {/* <div className="bg-card rounded-xl border border-border shadow-sm">
          <SupportingDocs pageRoute={pageRoute} clauseStandards={clauseStandards} pageClauses={pageClauses} />
      </div> */}
      {/* Add/Edit Group Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[600px] bg-secondary">
          <form onSubmit={(e) => !isReadOnly && handleSubmit(e)}>
            <DialogHeader>
              <DialogTitle>
                {isReadOnly ? 'View Special Interest Group' : editingGroup ? 'Edit Special Interest Group' : 'Add New Special Interest Group'}
              </DialogTitle>
              <DialogDescription>
                {isReadOnly ? 'View the details of this special interest group.'
                  : editingGroup ? 'Update the details of this special interest group.'
                    : 'Add details for a new special interest group.'}
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="groupName" className="text-right">
                  Group Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="groupName"
                  name="groupName"
                  value={formData.groupName}
                  onChange={(e) => !isReadOnly && handleInputChange(e)}
                  className={`col-span-3 ${isReadOnly ? 'opacity-100 text-foreground' : ''}`}
                  required
                  readOnly={isReadOnly}
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="primaryContact" className="text-right">
                  Primary Contact <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="primaryContact"
                  name="primaryContact"
                  value={formData.primaryContact}
                  onChange={(e) => !isReadOnly && handleInputChange(e)}
                  className={`col-span-3 ${isReadOnly ? 'opacity-100 text-foreground' : ''}`}
                  required
                  readOnly={isReadOnly}
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="contactEmail" className="text-right">
                  Contact Email <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="contactEmail"
                  name="contactEmail"
                  type="email"
                  value={formData.contactEmail}
                  onChange={(e) => !isReadOnly && handleInputChange(e)}
                  className={`col-span-3 ${isReadOnly ? 'opacity-100 text-foreground' : ''}`}
                  required
                  readOnly={isReadOnly}
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="contactPhone" className="text-right">
                  Contact Phone
                </Label>
                <Input
                  id="contactPhone"
                  name="contactPhone"
                  value={formData.contactPhone}
                  onChange={(e) => !isReadOnly && handleInputChange(e)}
                  className={`col-span-3 ${isReadOnly ? 'opacity-100 text-foreground' : ''}`}
                  readOnly={isReadOnly}
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="internalOwner" className="text-right">
                  Internal Owner
                </Label>
                <div className="col-span-3">
                  {isReadOnly ? (
                    <Input
                      value={formData.internalOwner ? userIdToName[formData.internalOwner] || formData.internalOwner : ''}
                      className="w-full opacity-100 text-foreground"
                      readOnly
                    />
                  ) : (
                    <SingleUserCombobox
                      selected={formData.internalOwner}
                      setSelected={handleOwnerSelect}
                      placeholder="Select an internal owner..."
                      className="w-full"
                    />
                  )}
                </div>
              </div>
              <div className="grid grid-cols-4 items-start gap-4">
                <Label htmlFor="comments" className="text-right mt-2">
                  Comments
                </Label>
                <div className="col-span-3">
                  <textarea
                    id="comments"
                    name="comments"
                    value={formData.comments}
                    onChange={(e) => !isReadOnly && handleInputChange(e)}
                    className={`flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${isReadOnly ? 'opacity-100 text-foreground' : ''}`}
                    rows={3}
                    readOnly={isReadOnly}
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsDialogOpen(false);
                  resetForm();
                }}
                disabled={isSubmitting}
              >
                {isReadOnly ? 'Close' : 'Cancel'}
              </Button>
              {!isReadOnly && (
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {editingGroup ? 'Updating...' : 'Adding...'}
                    </>
                  ) : editingGroup ? (
                    'Update Group'
                  ) : (
                    'Add Group'
                  )}
                </Button>
              )}
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
